import type { ColumnInfo, ItemInfo } from '../api/client'
import { CellView } from './BoardCell'

export interface ItemDetailPanelProps {
  item: ItemInfo
  columns: ColumnInfo[]
  onClose: () => void
  onPrev: () => void
  onNext: () => void
  hasPrev: boolean
  hasNext: boolean
  /** 正在编辑的列 key（同看板，由外层持有） */
  editingKey: string | null
  busy: boolean
  onStartEdit: (columnKey: string) => void
  onCancelEdit: () => void
  onCommitCell: (columnKey: string, value: string) => void
  /** 删除本条条目并关闭面板 */
  onDelete: () => void
}

/**
 * 条目详情面板（M6）。
 *
 * 设计要点：
 * - 700px 宽、贴右侧、整高，浮在看板之上（看板本身仍在下面，关闭即回）；
 * - 遮罩铺满其余区域，点击关闭；Esc 也关闭（由外层监听）；
 * - 第一版只做只读展示：标题 + 逐列「列名 / 当前值」。
 *   后续把 values 那一段换成 BoardCell 的编辑器即可获得完整编辑能力。
 */
export default function ItemDetailPanel({
  item,
  columns,
  onClose,
  onPrev,
  onNext,
  hasPrev,
  hasNext,
  editingKey,
  busy,
  onStartEdit,
  onCancelEdit,
  onCommitCell,
  onDelete,
}: ItemDetailPanelProps) {
  return (
    <>
      <div className="detail-mask" onClick={onClose} />
      <aside className="detail-panel" role="dialog" aria-modal="true" aria-label="条目详情">
        <header className="detail-panel__head">
          <span className="detail-panel__title">{item.name}</span>
          <div className="detail-panel__nav">
            <button
              type="button"
              className="detail-panel__btn detail-panel__btn--danger"
              title="删除条目"
              aria-label="删除条目"
              onClick={onDelete}
            >
              🗑
            </button>
            <button
              type="button"
              className="detail-panel__btn"
              disabled={!hasPrev}
              title="上一条"
              aria-label="上一条"
              onClick={onPrev}
            >
              ↑
            </button>
            <button
              type="button"
              className="detail-panel__btn"
              disabled={!hasNext}
              title="下一条"
              aria-label="下一条"
              onClick={onNext}
            >
              ↓
            </button>
            <button
              type="button"
              className="detail-panel__btn"
              title="关闭"
              aria-label="关闭"
              onClick={onClose}
            >
              ✕
            </button>
          </div>
        </header>

        <div className="detail-panel__body">
          {columns.map((c) => (
            <div key={c.id} className="detail-row">
              <span className="detail-row__label">{c.title}</span>
              <span className="detail-row__value">
                <CellView
                  column={c}
                  item={item}
                  editing={editingKey === c.key}
                  busy={busy}
                  onStartEdit={() => onStartEdit(c.key)}
                  onCommit={(v) => onCommitCell(c.key, v)}
                  onCancel={onCancelEdit}
                />
              </span>
            </div>
          ))}
        </div>
      </aside>
    </>
  )
}
